import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

const BackgroundBlobs = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
        <div className="absolute top-[-5%] right-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-orange-400 opacity-20 blur-3xl" />
        <div className="absolute top-[50%] left-[-15%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-red-400 opacity-10 blur-3xl transform -translate-y-1/2" />
    </div>
);

const ActivationPage = () => {
    const { uidb64, token } = useParams();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'fail'

    useEffect(() => {
        const activate = async () => {
            try {
                await axios.get(`/accounts/activate/${uidb64}/${token}/`);
                setStatus('success');
            } catch (err) {
                setStatus('fail');
            }
        };
        if (token) activate();
    }, [uidb64, token]);

    return (
        <div dir="rtl" className="bg-background text-slate-800 font-sans overflow-x-hidden max-w-[100vw] selection:bg-primary selection:text-white relative min-h-screen flex flex-col">
            <BackgroundBlobs />

            {/* Nav */}
            <nav className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center relative z-10">
                <div>
                    <div className="flex items-center select-none" dir="ltr">
                        <span className="font-logo font-light text-2xl sm:text-3xl text-slate-800 tracking-wide">BrokersConnect</span>
                    </div>
                </div>
            </nav>

            <div className="flex-1 flex flex-col justify-center items-center px-4 w-full relative z-10 pb-12 sm:pb-20">
                <div className="w-full max-w-md relative">
                    <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />

                    {/* Loading */}
                    {status === 'loading' && (
                        <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-orange-50 mb-6">
                                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">מפעיל את החשבון...</h2>
                            <p className="text-base text-slate-500">אנא המתן</p>
                        </div>
                    )}

                    {/* Success */}
                    {status === 'success' && (
                        <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-50 ring-4 ring-emerald-50/50 mb-6">
                                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">החשבון הופעל בהצלחה!</h2>
                            <p className="text-base text-slate-500 leading-relaxed mb-8">
                                החשבון שלך הופעל ומוכן לשימוש.<br />
                                כעת תוכל להתחבר למערכת.
                            </p>
                            <Link to="/login"
                                className="inline-block w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                                המשך להתחברות
                            </Link>
                        </div>
                    )}

                    {/* Fail */}
                    {status === 'fail' && (
                        <div className="relative bg-surface p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 ring-4 ring-red-50/50 mb-6">
                                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">ההפעלה נכשלה</h2>
                            <p className="text-base text-slate-500 leading-relaxed mb-8">
                                הקישור להפעלת החשבון אינו תקין או שכבר נעשה בו שימוש.<br />
                                <span className="text-sm opacity-80 mt-2 block">אנא פנה לתמיכה בכתובת support@brokersconnect.co.il</span>
                            </p>
                            <Link to="/login"
                                className="inline-block w-full bg-gradient-to-l from-primary to-amber-500 hover:from-primaryDark hover:to-primary text-white font-bold text-base sm:text-lg py-3.5 rounded-xl shadow-lg hover:shadow-primary/30 transform transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                                חזרה למסך ההתחברות
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivationPage;
